import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useState, useEffect } from 'react';
import { videoService } from '@/services/videoService';

export interface VideoForHookTrack {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  duration: number;
  plays: number;
  views: number;
  inUse?: boolean;
  inUseText?: string;
}

export const useHookTrackVideos = (userId: string | null) => {
  useLanguage();
  const [videos, setVideos] = useState<VideoForHookTrack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVideos = async () => {
      if (!userId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const fetchedVideos = await videoService.getDashboardVideos(userId);

        // Mapear para o formato esperado
        const mappedVideos: VideoForHookTrack[] = fetchedVideos.map((video) => {
          // Verificar se está em uso APENAS por Lead Test (Hook Track)
          const isInLeadTest = video.is_lead_test === true || video.is_lead_test === 'true' || video.is_lead_test === 1;

          return {
            id: video.id,
            title: video.title,
            thumbnailUrl: video.thumbnailUrl,
            duration: video.duration,
            plays: video.plays,
            views: video.views,
            inUse: isInLeadTest,
            inUseText: isInLeadTest ? 'Em teste de Hook' : undefined,
          };
        });

        // Mostrar TODOS os vídeos - não filtrar por duração
        setVideos(mappedVideos);
      } catch (err: any) {
        console.error('Erro ao buscar vídeos:', err);
        setError(err?.message || translateInterfaceText("Erro ao carregar vídeos"));
        setVideos([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchVideos();
  }, [userId]);

  return {
    videos,
    isLoading,
    error,
    refetch: () => {
      if (userId) {
        setIsLoading(true);
        videoService.getDashboardVideos(userId).then((fetchedVideos) => {
          const mappedVideos: VideoForHookTrack[] = fetchedVideos.map((video) => {
            // Verificar se está em uso APENAS por Lead Test (Hook Track)
            const isInLeadTest = video.is_lead_test === true || video.is_lead_test === 'true' || video.is_lead_test === 1;

            return {
              id: video.id,
              title: video.title,
              thumbnailUrl: video.thumbnailUrl,
              duration: video.duration,
              plays: video.plays,
              views: video.views,
              inUse: isInLeadTest,
              inUseText: isInLeadTest ? 'Em teste de Hook' : undefined,
            };
          });

          // Mostrar TODOS os vídeos - não filtrar por duração
          setVideos(mappedVideos);
          setIsLoading(false);
        }).catch((err) => {
          setError(err?.message || translateInterfaceText("Erro ao carregar vídeos"));
          setIsLoading(false);
        });
      }
    },
  };
};
